import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  
  useEffect(() => {
    // Check if the PWA has already been installed
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true;
    
    if (!isInstalled) {
      const handleBeforeInstallPrompt = (e: Event) => {
        // Prevent the default browser install prompt
        e.preventDefault();
        // Save the event for later use
        setDeferredPrompt(e);
        // Show the custom install button
        setShowPrompt(true);
      };
      
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      
      // Check if user recently dismissed the prompt (stored in localStorage)
      const promptDismissedTime = localStorage.getItem('pwaPromptDismissed');
      if (promptDismissedTime) {
        const dismissedTime = Number(promptDismissedTime);
        const currentTime = Date.now();
        const daysSinceDismissed = (currentTime - dismissedTime) / (1000 * 60 * 60 * 24);
        
        if (daysSinceDismissed < 7) {
          // If dismissed less than 7 days ago, don't show the prompt
          setShowPrompt(false);
        }
      }
      
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }
  }, []);
  
  const installApp = () => {
    if (!deferredPrompt) return;
    
    // Show the browser install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    deferredPrompt.userChoice.then((choiceResult: { outcome: string }) => {
      if (choiceResult.outcome === 'accepted') {
        // PWA was installed
        console.log('PWA installation accepted');
      } else {
        // PWA installation was declined
        console.log('PWA installation declined');
      }
      
      // Clear the deferredPrompt variable as it can only be used once
      setDeferredPrompt(null);
      setShowPrompt(false);
    });
  };
  
  const dismissPrompt = () => {
    setShowPrompt(false);
    // Store the current time in localStorage to prevent showing the prompt again for a while
    localStorage.setItem('pwaPromptDismissed', Date.now().toString());
  };
  
  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:bottom-4 md:w-80 bg-background border border-border shadow-lg rounded-lg p-4 z-40"
        >
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-semibold text-lg">Install Our App</h3>
            <button 
              onClick={dismissPrompt}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          
          <p className="text-muted-foreground text-sm mb-4">
            Add MakersPlace Educational Foundation to your home screen for quicker access to programmes, impact, and contact details.
          </p>
          
          <div className="flex space-x-2">
            <button
              onClick={dismissPrompt}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted transition-colors"
            >
              Not now
            </button>
            <button
              onClick={installApp}
              className="px-3 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
            >
              Install
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
